import { CommonModule } from '@angular/common';
import { Component, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  chevronDownOutline,
  chevronForwardOutline,
  copyOutline,
  cubeOutline,
  eyeOffOutline,
  eyeOutline,
  linkOutline,
  lockClosedOutline,
  lockOpenOutline,
  trashOutline
} from 'ionicons/icons';
import { InfoTooltipComponent } from '../info-tooltip/info-tooltip.component';
import { StudioColorInputComponent } from '../studio-color-input/studio-color-input.component';
import { STUDIO_COLOR_FIELD_CONFIGS } from '../studio-color-input/studio-color-input.config';
import { StudioMeasureInputComponent } from '../studio-measure-input/studio-measure-input.component';
import { StudioSpaceInputComponent } from '../studio-space-input/studio-space-input.component';
import { STUDIO_MEASURE_FIELD_CONFIGS } from '../studio-measure-input/studio-measure-input.config';
import { WebsiteBuilderService } from '../../services/website-builder.service';
import { ComponentCanvasThemeService } from '../../services/component-canvas-theme.service';
import {
  WebsiteBuilderDynamicComponentKind,
  WebsiteBuilderHoverEffect,
  WebsiteBuilderNode,
  WebsiteBuilderNodeContent,
  resolveWebsiteBuilderNodeStyle
} from '../../models/website-builder.models';
import { StudioNodeAnimation, StudioNodeStyle } from '../../models/component-studio.models';

const THEME_SCOPED_STYLE_FIELDS = new Set<keyof StudioNodeStyle>([
  'backgroundColor',
  'backgroundImage',
  'backgroundPosition',
  'backgroundSize',
  'backgroundRepeat',
  'color',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textTransform',
  'textDecoration',
  'whiteSpace',
  'wordBreak',
  'borderWidth',
  'borderStyle',
  'borderColor',
  'borderRadius',
  'outlineWidth',
  'outlineStyle',
  'outlineColor',
  'boxShadow',
  'filter',
  'backdropFilter',
  'opacity',
  'customCss'
]);

@Component({
  selector: 'app-website-builder-inspector',
  standalone: true,
  imports: [CommonModule, FormsModule, IonIcon, InfoTooltipComponent, StudioColorInputComponent, StudioMeasureInputComponent, StudioSpaceInputComponent],
  templateUrl: './website-builder-inspector.component.html',
  styleUrls: ['./website-builder-inspector.component.scss']
})
export class WebsiteBuilderInspectorComponent {
  readonly builderService = this.websiteBuilderService;
  readonly dataCollections = this.websiteBuilderService.dataCollections;
  readonly displayOptions = ['flex', 'block', 'grid', 'inline-flex', 'inline-block', 'inline', 'none'];
  readonly flexDirectionOptions = ['row', 'column', 'row-reverse', 'column-reverse'];
  readonly flexWrapOptions = ['nowrap', 'wrap', 'wrap-reverse'];
  readonly justifyOptions = ['flex-start', 'center', 'flex-end', 'space-between', 'space-around', 'space-evenly'];
  readonly alignOptions = ['stretch', 'flex-start', 'center', 'flex-end', 'baseline'];
  readonly alignSelfOptions = ['auto', 'stretch', 'flex-start', 'center', 'flex-end', 'baseline'];
  readonly textAlignOptions = ['left', 'center', 'right', 'justify'];
  readonly fontStyleOptions = ['normal', 'italic', 'oblique'];
  readonly textTransformOptions = ['none', 'uppercase', 'lowercase', 'capitalize'];
  readonly textDecorationOptions = ['none', 'underline', 'line-through', 'overline'];
  readonly whiteSpaceOptions = ['normal', 'nowrap', 'pre', 'pre-wrap', 'pre-line', 'break-spaces'];
  readonly wordBreakOptions = ['normal', 'break-word', 'break-all', 'keep-all'];
  readonly borderStyleOptions = ['none', 'solid', 'dashed', 'dotted', 'double'];
  readonly backgroundRepeatOptions = ['repeat', 'no-repeat', 'repeat-x', 'repeat-y', 'round', 'space'];
  readonly overflowOptions = ['visible', 'hidden', 'auto', 'scroll', 'clip'];
  readonly visibilityOptions = ['visible', 'hidden'];
  readonly pointerEventsOptions = ['auto', 'none'];
  readonly cursorOptions = ['auto', 'pointer', 'text', 'move', 'grab', 'not-allowed', 'help', 'crosshair'];
  readonly objectFitOptions = ['cover', 'contain', 'scale-down', 'none'];
  readonly positionOptions = ['static', 'relative', 'absolute', 'sticky', 'fixed'] as const;
  readonly linkKindOptions = [
    { id: 'none', label: 'None' },
    { id: 'external-url', label: 'External URL' },
    { id: 'internal-page', label: 'Internal page' }
  ] as const;
  readonly hoverEffectOptions: Array<{ id: WebsiteBuilderHoverEffect; label: string }> = [
    { id: 'none', label: 'None' },
    { id: 'lift', label: 'Lift on hover' },
    { id: 'grow', label: 'Grow on hover' },
    { id: 'glow', label: 'Glow on hover' }
  ];
  readonly dynamicComponentOptions: Array<{ id: WebsiteBuilderDynamicComponentKind; label: string }> = [
    { id: 'none', label: 'None' },
    { id: 'collection-list', label: 'Collection list' },
    { id: 'catalog-grid', label: 'Catalog grid' },
    { id: 'filter-panel', label: 'Filter panel' },
    { id: 'storefront-runtime', label: 'Storefront runtime' }
  ];
  readonly animationOptions = ['none', 'fade-up', 'fade-in', 'pop-in', 'pulse', 'float'] as const;
  readonly easingOptions = ['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear'];
  readonly fontWeightOptions = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];
  readonly sectionOrder = ['selected', 'content', 'dynamic', 'interaction', 'layout', 'surface', 'transform', 'animation', 'advanced'] as const;
  readonly colorFieldConfigs = STUDIO_COLOR_FIELD_CONFIGS;
  readonly measureFieldConfigs = STUDIO_MEASURE_FIELD_CONFIGS;
  private readonly openSectionStorageKey = 'bw.website-builder.inspector.open-section';
  private openSectionId: (typeof this.sectionOrder)[number] | null = this.restoreOpenSection();

  constructor(
    private readonly websiteBuilderService: WebsiteBuilderService,
    private readonly canvasThemeService: ComponentCanvasThemeService
  ) {
    addIcons({
      chevronDownOutline,
      chevronForwardOutline,
      copyOutline,
      cubeOutline,
      eyeOffOutline,
      eyeOutline,
      linkOutline,
      lockClosedOutline,
      lockOpenOutline,
      trashOutline
    });

    effect(() => {
      const selectedNodeId = this.websiteBuilderService.selectedNodeId();
      if (!selectedNodeId) {
        return;
      }

      if (!this.openSectionId || !this.sectionOrder.includes(this.openSectionId)) {
        this.setOpenSection('selected');
      }
    });
  }

  updateLabel(value: string): void {
    this.websiteBuilderService.updateSelectedNodeLabel(value);
  }

  updateContent(field: keyof WebsiteBuilderNodeContent, value: string): void {
    this.websiteBuilderService.updateSelectedNodeContent({ [field]: value } as Partial<WebsiteBuilderNodeContent>);
  }

  updateOptionalContent(field: keyof WebsiteBuilderNodeContent, value: string): void {
    const nextValue = value.trim();
    this.websiteBuilderService.updateSelectedNodeContent({
      [field]: nextValue.length > 0 ? nextValue : undefined
    } as Partial<WebsiteBuilderNodeContent>);
  }

  updateOptionalContentNumber(field: 'dataItemLimit', value: string): void {
    const parsedValue = Number(value);
    this.websiteBuilderService.updateSelectedNodeContent({
      [field]: Number.isFinite(parsedValue) ? Math.max(0, Math.trunc(parsedValue)) : undefined
    });
  }

  updateDynamicComponent(value: string): void {
    if (this.dynamicComponentOptions.some((option) => option.id === value)) {
      this.websiteBuilderService.updateSelectedNodeContent({
        dynamicComponent: value as WebsiteBuilderDynamicComponentKind
      });
    }
  }

  styleValue<K extends keyof StudioNodeStyle>(node: WebsiteBuilderNode, field: K): StudioNodeStyle[K] {
    return resolveWebsiteBuilderNodeStyle(
      node,
      this.canvasThemeService.selectedPackageId(),
      this.canvasThemeService.previewMode()
    )[field];
  }

  updateStyle(field: keyof StudioNodeStyle, value: string): void {
    this.updateScopedStyle(field, { [field]: value } as Partial<StudioNodeStyle>);
  }

  updateNumericStyle(field: keyof StudioNodeStyle, value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.updateScopedStyle(field, { [field]: parsedValue } as Partial<StudioNodeStyle>);
  }

  private updateScopedStyle(field: keyof StudioNodeStyle, patch: Partial<StudioNodeStyle>): void {
    if (THEME_SCOPED_STYLE_FIELDS.has(field)) {
      this.websiteBuilderService.updateSelectedNodeStyleVariant(
        this.canvasThemeService.selectedPackageId(),
        this.canvasThemeService.previewMode(),
        patch
      );
      return;
    }

    this.websiteBuilderService.updateSelectedNodeStyle(patch);
  }

  updateTransform(field: 'translateX' | 'translateY' | 'scaleX' | 'scaleY' | 'rotate' | 'skewX' | 'skewY', value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.websiteBuilderService.updateSelectedNodeTransform({ [field]: parsedValue });
  }

  updateAnimationNumber(field: 'durationMs' | 'delayMs', value: string): void {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }

    this.websiteBuilderService.updateSelectedNodeAnimation({ [field]: parsedValue });
  }

  updateAnimationText(field: 'name' | 'easing' | 'iterationCount', value: string): void {
    this.websiteBuilderService.updateSelectedNodeAnimation({ [field]: value } as Partial<StudioNodeAnimation>);
  }

  updateLinkKind(value: string): void {
    if (value === 'none' || value === 'external-url' || value === 'internal-page') {
      this.websiteBuilderService.setSelectedNodeLinkKind(value);
    }
  }

  updateInternalTarget(value: string): void {
    const nextValue = value.trim();
    this.websiteBuilderService.setSelectedNodeInternalTarget(nextValue.length > 0 ? nextValue : null);
  }

  updateOpenInNewTab(enabled: boolean): void {
    this.websiteBuilderService.setSelectedNodeOpenInNewTab(enabled);
  }

  updateHoverEffect(value: string): void {
    if (value === 'none' || value === 'lift' || value === 'grow' || value === 'glow') {
      this.websiteBuilderService.setSelectedNodeHoverEffect(value);
    }
  }

  toggleHidden(): void {
    this.websiteBuilderService.toggleSelectedHidden();
  }

  toggleLocked(): void {
    this.websiteBuilderService.toggleSelectedLocked();
  }

  duplicateSelected(): void {
    this.websiteBuilderService.duplicateSelectedNode();
  }

  removeSelected(): void {
    this.websiteBuilderService.removeSelectedNode();
  }

  saveSelectedAsComponent(): void {
    this.websiteBuilderService.saveSelectedNodeAsComponent();
  }

  canRemoveSelected(node: WebsiteBuilderNode | null): boolean {
    return this.websiteBuilderService.canRemoveSelected(node);
  }

  canSaveSelectedAsComponent(node: WebsiteBuilderNode | null): boolean {
    return this.websiteBuilderService.canSaveSelectedAsComponent(node);
  }

  showsInteractionSection(node: WebsiteBuilderNode | null): boolean {
    return !!node;
  }

  showsDynamicSection(node: WebsiteBuilderNode | null): boolean {
    return !!node && (
      node.type === 'commerce' ||
      (node.content.dynamicComponent ?? 'none') !== 'none' ||
      !!node.content.dataCollectionKey ||
      !!node.content.dataBinding ||
      !!node.content.dataAction ||
      !!node.content.repeatTemplateKey ||
      !!node.content.repeatPartKey
    );
  }

  isSectionOpen(sectionId: (typeof this.sectionOrder)[number]): boolean {
    return this.openSectionId === sectionId;
  }

  toggleSection(sectionId: (typeof this.sectionOrder)[number]): void {
    this.setOpenSection(this.openSectionId === sectionId ? null : sectionId);
  }

  private setOpenSection(sectionId: (typeof this.sectionOrder)[number] | null): void {
    this.openSectionId = sectionId;

    if (typeof window === 'undefined') {
      return;
    }

    if (!sectionId) {
      window.localStorage.removeItem(this.openSectionStorageKey);
      return;
    }

    window.localStorage.setItem(this.openSectionStorageKey, sectionId);
  }

  private restoreOpenSection(): (typeof this.sectionOrder)[number] | null {
    if (typeof window === 'undefined') {
      return 'selected';
    }

    const storedValue = window.localStorage.getItem(this.openSectionStorageKey);
    if (storedValue && this.sectionOrder.includes(storedValue as (typeof this.sectionOrder)[number])) {
      return storedValue as (typeof this.sectionOrder)[number];
    }

    return 'selected';
  }
}
